import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { api } from '@/api/client'
import { BASE_MEU_ROBO, type Robo } from '@/api/robo'
import { useAuthStore } from '@/store/auth'
import type { AlertRow } from './RoboAlertTable'

export type Evidencia = {
  age_seconds: number; max_age_seconds: number; collected_at: number; active_count: number; alerts: AlertRow[]
}

const negadoPor = (erro: unknown) => isAxiosError(erro) && [403, 404].includes(erro.response?.status ?? 0)
const retryTransient = (count: number, error: unknown) =>
  count < 1 && !(isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0))

/**
 * A coleta de alertas do robô do dono. "Recente" é pela idade que o SERVIDOR diz
 * mais o tempo desde a resposta — nunca pelo relógio do navegador contra
 * `collected_at`, que pode estar em outra época. Permissão revogada (403/404)
 * esconde o que estava em cache: `evidencia` volta `undefined`.
 */
export function useEvidenciaDoRobo(robo: Robo | null, ativo: boolean) {
  const userId = useAuthStore((s) => s.user?.id)
  const [now, setNow] = useState(performance.now())
  const [recebidoEm, setRecebidoEm] = useState(performance.now())
  useEffect(() => {
    if (!ativo) return
    const t = setInterval(() => setNow(performance.now()), 1000)
    return () => clearInterval(t)
  }, [ativo])
  const consulta = useQuery({
    queryKey: ['robo-alerts', userId, robo?.public_id],
    enabled: ativo && !!robo,
    queryFn: async () => (await api.get<Evidencia>(`${BASE_MEU_ROBO}/alerts`)).data,
    refetchInterval: (q) => q.state.error || !q.state.data ? 15000
      : Math.min(15000, Math.max(1000, (q.state.data.max_age_seconds - q.state.data.age_seconds - 2) * 1000)),
    retry: retryTransient,
  })
  useEffect(() => { setRecebidoEm(performance.now()); setNow(performance.now()) }, [consulta.dataUpdatedAt])
  const negado = negadoPor(consulta.error)
  const evidencia = negado ? undefined : consulta.data
  const fresh = !!evidencia && !consulta.isError
    && evidencia.age_seconds + Math.max(0, now - recebidoEm) / 1000 <= evidencia.max_age_seconds
  return {
    evidencia, fresh, negado,
    atualizar: () => { void consulta.refetch() },
    atualizando: consulta.isFetching,
  }
}
